/**
 * RandomGen.ts — Seeded RNG and name generators for Gallowcrest.
 *
 * Extends the Ascend base with dice rolling methods for D&D-style skill checks.
 * Removes Ascend-specific generators (kingdom, faction, weapon, armor).
 * Adds Gallowcrest-specific hero name generation.
 */

// --- Seeded Random Number Generator ---

export class SeededRNG {
  private state: number;

  constructor(seed?: number) {
    this.state = seed ?? Math.floor(Math.random() * 2147483647);
  }

  /** Get the current seed */
  getSeed(): number {
    return this.state;
  }

  /** Generate a random float between 0 and 1 */
  next(): number {
    let t = (this.state += 0x6d2b79f5);
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  }

  /** Generate a random integer between min and max (inclusive) */
  nextInt(min: number, max: number): number {
    return Math.floor(this.next() * (max - min + 1)) + min;
  }

  /** Pick a random element from an array */
  pick<T>(array: readonly T[]): T {
    return array[Math.floor(this.next() * array.length)];
  }

  /** Shuffle an array in place */
  shuffle<T>(array: T[]): T[] {
    for (let i = array.length - 1; i > 0; i--) {
      const j = Math.floor(this.next() * (i + 1));
      [array[i], array[j]] = [array[j], array[i]];
    }
    return array;
  }

  /** Roll with a weighted chance (0-1). Returns true if the roll succeeds. */
  chance(probability: number): boolean {
    return this.next() < probability;
  }

  // --- Dice Rolling (D&D Style) ---

  /** Roll a dN die (e.g., d20, d6, d12) */
  rollDie(sides: number): number {
    return this.nextInt(1, sides);
  }

  /** Roll a d20 */
  d20(): number {
    return this.rollDie(20);
  }

  /** Roll NdS (e.g., 2d6 = rollDice(2, 6)) */
  rollDice(count: number, sides: number): number {
    let total = 0;
    for (let i = 0; i < count; i++) {
      total += this.rollDie(sides);
    }
    return total;
  }

  /** Roll a d20 with a modifier and compare against DC */
  rollCheck(modifier: number, dc: number): { roll: number; modifier: number; total: number; dc: number; success: boolean } {
    const roll = this.d20();
    const total = roll + modifier;
    return {
      roll,
      modifier,
      total,
      dc,
      success: total >= dc,
    };
  }
}

// --- Name Components ---

const HERO_PREFIXES = [
  'Aed', 'Bal', 'Bran', 'Cael', 'Cor', 'Dar', 'Dun', 'Eld', 'Eow', 'Fen',
  'Gar', 'Grim', 'Hal', 'Isen', 'Kel', 'Kael', 'Lor', 'Mal', 'Mor', 'Nar',
  'Orm', 'Ost', 'Ral', 'Ren', 'Sig', 'Sten', 'Thal', 'Thorn', 'Ulf', 'Val',
  'Wulf', 'Yar', 'Zar', 'Ash', 'Bern', 'Cad', 'Dael', 'Egan', 'Frey', 'Gael',
  'Harn', 'Ivar', 'Jorn', 'Kord', 'Leif', 'Mael', 'Nord', 'Orin', 'Penn', 'Rael',
];

const HERO_SUFFIXES = [
  'ric', 'mund', 'gar', 'wen', 'dor', 'mir', 'ald', 'orn', 'ard',
  'bert', 'fred', 'rik', 'win', 'mar', 'helm', 'brand', 'grim', 'stead',
  'ton', 'vik', 'ulf', 'sten', 'ran', 'var', 'heim', 'wald',
  'thor', 'bald', 'red', 'wyn', 'oak', 'hart', 'fell', 'den', 'ley', 'mere',
];

// --- Name Generator ---

export class NameGenerator {
  private rng: SeededRNG;

  constructor(rng?: SeededRNG) {
    this.rng = rng ?? new SeededRNG();
  }

  /** Generate a fantasy hero name */
  heroName(): string {
    const prefix = this.rng.pick(HERO_PREFIXES);
    const suffix = this.rng.pick(HERO_SUFFIXES);
    return prefix + suffix;
  }
}
